import { useState, useRef } from "react";
import { toast } from "sonner";
import { Briefcase, Send, MapPin, Upload, Loader2, FileText, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal, Label } from "@/components/site/Motion";
import { PageHero } from "@/components/site/Sections";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const OPENINGS = [
  { role: "Site Civil Engineer", type: "Full-time", loc: "Lucknow" },
  { role: "Interior Designer", type: "Full-time", loc: "Lucknow" },
  { role: "3D Visualiser / Architect", type: "Full-time", loc: "Lucknow" },
  { role: "Project Supervisor", type: "Full-time", loc: "Lucknow / UP" },
  { role: "Sales & Client Relations", type: "Full-time", loc: "Lucknow" },
  { role: "Site Supervisor / Junior Engineer", type: "Full-time", loc: "Lucknow / UP" },
];

export default function Careers() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", role: "", message: "", cv_url: "" });
  const [loading, setLoading] = useState(false);
  const [cvName, setCvName] = useState("");
  const [cvBusy, setCvBusy] = useState(false);
  const cvRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCv = async (file) => {
    if (!file) return;
    setCvBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/careers/upload-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cv_url: data.url }));
      setCvName(file.name);
      toast.success("Resume attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setCvBusy(false);
      if (cvRef.current) cvRef.current.value = "";
    }
  };

  const removeCv = () => { setForm((f) => ({ ...f, cv_url: "" })); setCvName(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please share your name and phone number."); return; }
    setLoading(true);
    try {
      await api.post("/careers", form);
      toast.success("Application received! Our team will reach out to you.");
      setForm({ name: "", phone: "", email: "", role: "", message: "", cv_url: "" });
      setCvName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        eyebrow="Careers"
        title="Build your career with UYI."
        subtitle="Join a team that's shaping skylines and interiors across Lucknow & Uttar Pradesh. We're always looking for driven, detail-obsessed people."
        image="https://images.unsplash.com/photo-1577199001468-44c049e7603f?crop=entropy&cs=srgb&fm=jpg&q=85"
      />

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-6">
            <Label>Current openings</Label>
            <div className="mt-8 space-y-3">
              {OPENINGS.map((o, i) => (
                <Reveal key={o.role} delay={i * 0.05}>
                  <button
                    onClick={() => { setForm((f) => ({ ...f, role: o.role })); document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" }); }}
                    data-testid={`career-opening-${i}`}
                    className="group w-full flex items-center justify-between gap-4 rounded-sm border border-navy/10 bg-white p-5 text-left hover:border-navy transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-navy text-gold"><Briefcase className="h-5 w-5" /></span>
                      <div>
                        <div className="font-display text-lg text-navy">{o.role}</div>
                        <div className="text-xs text-navy/50 flex items-center gap-2 mt-0.5"><MapPin className="h-3 w-3" /> {o.loc} · {o.type}</div>
                      </div>
                    </div>
                    <span className="text-sm text-navy/50 group-hover:text-gold transition-colors">Apply →</span>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="md:col-span-6" id="apply">
            <form onSubmit={submit} data-testid="career-form" className="rounded-sm border border-navy/10 bg-white p-8 lg:p-10">
              <h3 className="font-display text-2xl text-navy mb-6">Apply now</h3>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name *"><Input data-testid="career-name" value={form.name} onChange={set("name")} className="rounded-sm" /></Field>
                <Field label="Phone *"><Input data-testid="career-phone" value={form.phone} onChange={set("phone")} placeholder="+91" className="rounded-sm" /></Field>
                <Field label="Email"><Input data-testid="career-email" type="email" value={form.email} onChange={set("email")} className="rounded-sm" /></Field>
                <Field label="Role"><Input data-testid="career-role" value={form.role} onChange={set("role")} placeholder="Position you're applying for" className="rounded-sm" /></Field>
              </div>
              <div className="mt-5">
                <Field label="Message / experience">
                  <Textarea data-testid="career-message" value={form.message} onChange={set("message")} rows={4} placeholder="Tell us about your experience…" className="rounded-sm" />
                </Field>
              </div>
              <div className="mt-5">
                <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">Resume / CV</span>
                {form.cv_url ? (
                  <div className="flex items-center justify-between gap-3 rounded-sm border border-navy/15 bg-cream px-4 py-3" data-testid="career-cv-attached">
                    <span className="flex items-center gap-2 text-sm text-navy truncate"><FileText className="h-4 w-4 text-gold shrink-0" /> {cvName || "Resume attached"}</span>
                    <button type="button" onClick={removeCv} data-testid="career-cv-remove" className="text-navy/40 hover:text-red-500 transition-colors"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => cvRef.current?.click()}
                    data-testid="career-cv-upload"
                    className="w-full flex items-center justify-center gap-2 rounded-sm border-2 border-dashed border-navy/20 px-4 py-4 text-sm text-navy/60 hover:border-gold hover:text-gold transition-colors"
                  >
                    {cvBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {cvBusy ? "Uploading…" : "Attach resume (PDF, DOC, DOCX, JPG, PNG · max 10MB)"}
                  </button>
                )}
                <input ref={cvRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={(e) => handleCv(e.target.files?.[0])} />
              </div>
              <button type="submit" data-testid="career-submit" disabled={loading || cvBusy} className="mt-7 inline-flex items-center gap-3 rounded-sm bg-navy px-8 py-4 text-white hover:bg-gold hover:text-navy transition-colors duration-300 disabled:opacity-60">
                {loading ? "Submitting…" : "Submit application"} <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">{label}</span>
      {children}
    </label>
  );
}
